import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Trash2, Archive, Info } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { useT } from '@/i18n/I18nProvider'

export interface ConfirmOptions {
  title: string
  description?: ReactNode
  confirmText?: string
  cancelText?: string
  tone?: 'default' | 'destructive' | 'warning'
  icon?: 'trash' | 'archive' | 'warning' | 'info'
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

const ICONS = { trash: Trash2, archive: Archive, warning: AlertTriangle, info: Info }

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useT()
  const [open, setOpen] = useState(false)
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((v: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  function settle(value: boolean) {
    setOpen(false)
    resolver.current?.(value)
    resolver.current = null
  }

  const tone = opts?.tone ?? 'default'
  const iconKey = opts?.icon ?? (tone === 'destructive' ? 'trash' : tone === 'warning' ? 'warning' : 'info')
  const Icon = ICONS[iconKey]

  const iconWrap =
    tone === 'destructive'
      ? 'bg-destructive/10 text-destructive'
      : tone === 'warning'
        ? 'bg-amber-500/10 text-amber-500'
        : 'bg-primary/10 text-primary'

  const actionCls =
    tone === 'destructive'
      ? 'bg-destructive text-white hover:bg-destructive/90'
      : tone === 'warning'
        ? 'bg-amber-500 text-white hover:bg-amber-500/90'
        : ''

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => !v && settle(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-start gap-3">
              <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-full', iconWrap)}>
                <Icon className="size-5" />
              </span>
              <div className="space-y-1.5">
                <AlertDialogTitle>{opts?.title}</AlertDialogTitle>
                {opts?.description && (
                  <AlertDialogDescription className="whitespace-pre-line">{opts.description}</AlertDialogDescription>
                )}
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>{opts?.cancelText ?? t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className={actionCls} onClick={() => settle(true)}>
              {opts?.confirmText ?? t('dlg.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm deve ser usado dentro de ConfirmProvider')
  return ctx
}
